import { useEffect, useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { MasterControlAlertSettings } from "@/hooks/useMasterControl";
import MCAlertDestination from "./MCAlertDestination";
import MCAlertToggle from "./MCAlertToggle";

type MCAlertsPanelProps = {
  settings: MasterControlAlertSettings | null;
  onSave: (settings: MasterControlAlertSettings) => Promise<void>;
  onTest: () => Promise<void>;
  onReload?: () => Promise<void>;
};

const DEFAULT_ALERT_SETTINGS: MasterControlAlertSettings = {
  slackEnabled: false,
  slackWebhook: "",
  emailEnabled: false,
  emailAddress: "",
  webhookEnabled: false,
  webhookUrl: "",
};

export function MCAlertsPanel({ settings, onSave, onTest, onReload }: MCAlertsPanelProps) {
  const [local, setLocal] = useState<MasterControlAlertSettings | null>(settings);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLocal(settings);
  }, [settings]);

  const resolved = local ?? settings ?? DEFAULT_ALERT_SETTINGS;
  const ready = Boolean(settings);

  const dirty = useMemo(() => {
    if (!settings || !local) return false;
    return (
      settings.slackEnabled !== local.slackEnabled ||
      (settings.slackWebhook ?? "") !== (local.slackWebhook ?? "") ||
      settings.emailEnabled !== local.emailEnabled ||
      (settings.emailAddress ?? "") !== (local.emailAddress ?? "") ||
      settings.webhookEnabled !== local.webhookEnabled ||
      (settings.webhookUrl ?? "") !== (local.webhookUrl ?? "")
    );
  }, [local, settings]);

  const update = <K extends keyof MasterControlAlertSettings>(key: K, value: MasterControlAlertSettings[K]) => {
    setLocal((prev) => {
      const base = prev ?? settings ?? DEFAULT_ALERT_SETTINGS;
      return { ...base, [key]: value };
    });
  };

  const handleSave = async () => {
    if (!local) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(local);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to save alert settings.");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setError(null);
    try {
      await onTest();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to send test alert.");
    } finally {
      setTesting(false);
    }
  };

  const handleReload = async () => {
    if (!onReload) return;
    setReloading(true);
    setError(null);
    try {
      await onReload();
      setLocal(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to reload alert settings.");
    } finally {
      setReloading(false);
    }
  };

  return (
    <Card className="rounded-2xl border border-border bg-background/60 shadow-none">
      <CardHeader className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <CardTitle className="text-lg font-semibold text-foreground">System alerts &amp; notifications</CardTitle>
          <p className="text-sm text-muted-foreground">Dispatch error notifications to Slack, email, and custom webhooks.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {onReload && (
            <Button variant="outline" size="sm" onClick={() => void handleReload()} disabled={!ready || reloading}>
              {reloading ? "Reloading…" : "Reload"}
            </Button>
          )}
          <Button
            size="sm"
            onClick={() => void handleSave()}
            disabled={!ready || !dirty || saving}
            className="bg-emerald-600 hover:bg-emerald-500"
          >
            {saving ? "Saving…" : "Save alert settings"}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void handleTest()}
            disabled={!ready || testing}
            className="gap-2"
          >
            {testing ? "Sending…" : "Send test alert"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && <p className="text-sm text-rose-300">{error}</p>}
        {!ready && <p className="text-sm text-muted-foreground">Loading alert settings…</p>}
        {ready && (
          <div className="space-y-4">
            <div className="grid gap-4 lg:grid-cols-3">
              <MCAlertToggle
                title="Slack alerts"
                description="Send owner alerts to Slack."
                value={resolved.slackEnabled}
                onChange={(checked) => update("slackEnabled", checked)}
                disabled={saving}
              />
              <MCAlertToggle
                title="Email alerts"
                description="Send email notifications for critical events."
                value={resolved.emailEnabled}
                onChange={(checked) => update("emailEnabled", checked)}
                disabled={saving}
              />
              <MCAlertToggle
                title="Webhook alerts"
                description="POST payloads to custom observability endpoints."
                value={resolved.webhookEnabled}
                onChange={(checked) => update("webhookEnabled", checked)}
                disabled={saving}
              />
            </div>
            <div className="grid gap-4 lg:grid-cols-3">
              <MCAlertDestination
                label="Slack webhook URL"
                description="Use Slack incoming webhook for owner channel."
                placeholder="https://hooks.slack.com/services/..."
                value={resolved.slackWebhook ?? ""}
                disabled={saving || !resolved.slackEnabled}
                onChange={(value) => update("slackWebhook", value)}
              />
              <MCAlertDestination
                label="Email address"
                description="Alerts sent to this address."
                placeholder="owner@example.com"
                value={resolved.emailAddress ?? ""}
                disabled={saving || !resolved.emailEnabled}
                onChange={(value) => update("emailAddress", value)}
              />
              <MCAlertDestination
                label="Custom webhook URL"
                description="Optional HTTP destination for alert payloads."
                placeholder="https://example.com/alerts"
                value={resolved.webhookUrl ?? ""}
                disabled={saving || !resolved.webhookEnabled}
                onChange={(value) => update("webhookUrl", value)}
              />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default MCAlertsPanel;
